// Scope is the application's main axis (ZENITH.md §18): organization, then
// customer, then site. It lives in the address — `?org=…&customer=…&site=…`
// on whatever page is open — so a link opens on the scope its sender was
// looking at, the back button undoes a pick, and two tabs hold two scopes.
// The last pick is also remembered per browser, and read once, at load, when
// the address says nothing; it is dropped at sign-out so the next person on
// the browser does not inherit a site they cannot see. Names are looked up
// live from the lists, never stored, so a rename shows immediately.
//
// Beside the scope sit the page facets (`facets.ts`, #254): the one device,
// network or audit target a page is narrowed to. The links here carry a
// facet only to a page that understands it, and picking another site drops
// the facets that named something inside the site being left.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate, type LinkProps, type NavLinkProps } from "react-router-dom";
import { api } from "../api/client";
import { carryFacets, facetSearch, leaveSite, type Facets } from "./facets";

export interface Scope { orgId?: string; customerId?: string; siteId?: string }

interface ScopeCtx {
  scope: Scope;
  setScope(next: Scope): void;
  /** Where `setScope(next)` goes: this page, at `next`. A link that picks a
   * scope points here, so its `href` and its click are one destination. */
  pickTarget(next: Scope): { pathname: string; search: string };
  /** The query string that names `scope`, for a link that should keep it. */
  search: string;
  /** The application opened on an address naming no scope, in a browser that
   * remembered none: the one moment a default scope may be chosen for the
   * person (`onlyScope.ts`, UX-006). False once anything was picked. */
  fresh: boolean;
}
const Ctx = createContext<ScopeCtx | undefined>(undefined);
const KEY = "zenith.scope";
const PARAMS: [keyof Scope, string][] = [["orgId", "org"], ["customerId", "customer"], ["siteId", "site"]];

/** Narrowing below a level clears everything beneath it. */
function normalize(next: Scope): Scope {
  return {
    orgId: next.orgId || undefined,
    customerId: next.orgId ? next.customerId || undefined : undefined,
    siteId: next.orgId && next.customerId ? next.siteId || undefined : undefined,
  };
}

/** The scope an address names; `undefined` when it names none. An address
 * with no scope parameter at all is "all sites" once the application is
 * running, and "whatever was last picked" only at load. */
export function parseScope(search: string): Scope | undefined {
  const p = new URLSearchParams(search);
  if (!PARAMS.some(([, name]) => p.has(name))) return undefined;
  return normalize({ orgId: p.get("org") ?? undefined, customerId: p.get("customer") ?? undefined, siteId: p.get("site") ?? undefined });
}

/** `search` with the scope parameters replaced by `scope`'s; every other
 * parameter is kept. All sites is written as none of them. */
export function scopeSearch(scope: Scope, search = ""): string {
  const p = new URLSearchParams(search);
  for (const [field, name] of PARAMS) {
    const v = scope[field];
    if (v) p.set(name, v); else p.delete(name);
  }
  const s = p.toString();
  return s ? "?" + s : "";
}

/** The level an address names without the levels above it, which `normalize`
 * would drop: `?site=` alone, or `?customer=` alone (UX-005). A link pasted
 * from a ticket names the site and nothing else, and the site's own row knows
 * its customer and organization, so the address is completed from it rather
 * than opened at all sites. */
export function partialScope(search: string): { siteId: string } | { customerId: string } | undefined {
  const p = new URLSearchParams(search);
  const org = p.get("org") || undefined;
  const customer = p.get("customer") || undefined;
  const site = p.get("site") || undefined;
  if (site && !(org && customer)) return { siteId: site };
  if (customer && !org) return { customerId: customer };
  return undefined;
}

/** Whether the browser remembers a scope at all, "all sites" included. */
function remembers(): boolean {
  try { return localStorage.getItem(KEY) !== null; } catch { return false; }
}

function load(): Scope {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as Scope;
    return normalize({ orgId: v.orgId, customerId: v.customerId, siteId: v.siteId });
  } catch { return {}; }
}

/** Forgets the remembered scope; called at sign-out. */
export function forgetScope() {
  try { localStorage.removeItem(KEY); } catch { /* private mode */ }
}

const isEmpty = (s: Scope) => !s.orgId && !s.customerId && !s.siteId;

export function ScopeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const fromUrl = useMemo(() => parseScope(location.search), [location.search]);
  // Load order: the address, then what the browser remembers, then nothing.
  // Only the first address the application opens on falls through to
  // storage; after that an address with no scope means all sites, so a link
  // that forgot to carry one shows everything rather than someone's last pick.
  // "First" is the router's first location key, captured once; the restore
  // itself is a replace, which changes the key and ends the restoring state.
  const [first] = useState(() => ({
    key: location.key,
    remembered: fromUrl === undefined ? load() : undefined,
    fresh: fromUrl === undefined && !remembers(),
  }));
  const restoring = location.key === first.key && first.remembered !== undefined && !isEmpty(first.remembered);
  useEffect(() => {
    if (restoring) navigate({ pathname: location.pathname, search: scopeSearch(first.remembered!, location.search) }, { replace: true });
  }, [restoring, first, navigate, location.pathname, location.search]);

  // An address naming a site (or a customer) without the levels above it is
  // completed from that row's own read, and replaced, before anything is
  // drawn (UX-005). A row the caller cannot read answers 404 like one that does
  // not exist (§6.3), and either way the address is left as it was and opens
  // at all sites, as it did before: nothing is disclosed that was not, and the
  // rest of the address is not touched.
  const { pathname, search: here, hash } = location;
  const [unresolved, setUnresolved] = useState<string>();
  const partial = useMemo(() => (here === unresolved ? undefined : partialScope(here)), [here, unresolved]);
  useEffect(() => {
    if (!partial) return;
    let live = true;
    const go = (to: Scope) => { if (live) navigate({ pathname, search: scopeSearch(to, here), hash }, { replace: true }); };
    const none = () => { if (live) setUnresolved(here); };
    if ("siteId" in partial) {
      api.GET("/sites/{id}", { params: { path: { id: partial.siteId } } }).then(({ data }) =>
        data ? go({ orgId: data.org_id, customerId: data.customer_id, siteId: data.id }) : none(), none);
    } else {
      api.GET("/customers/{id}", { params: { path: { id: partial.customerId } } }).then(({ data }) =>
        data ? go({ orgId: data.org_id, customerId: data.id }) : none(), none);
    }
    return () => { live = false; };
  }, [partial, navigate, pathname, here, hash]);

  const scope = useMemo(() => fromUrl ?? {}, [fromUrl]);
  useEffect(() => {
    if (restoring || partial) return;
    try { localStorage.setItem(KEY, JSON.stringify(scope)); } catch { /* private mode */ }
  }, [scope, restoring, partial]);

  const value = useMemo<ScopeCtx>(() => {
    const pickTarget = (next: Scope) => {
      const to = normalize(next);
      // Leaving the site takes the site's facets with it: the device or
      // network they named is not at the site being picked.
      const rest = to.siteId === scope.siteId ? location.search : leaveSite(location.search);
      return { pathname: location.pathname, search: scopeSearch(to, rest) };
    };
    // Fresh until the scope is anything but all sites: a pick, a link that
    // carried one, or the default `onlyScope` chose, each ends it.
    return { scope, search: scopeSearch(scope), pickTarget, setScope: (next) => navigate(pickTarget(next)), fresh: first.fresh && isEmpty(scope) };
  }, [scope, location.pathname, location.search, navigate, first.fresh]);
  if (restoring || partial) return null;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The scope where there is one, for a component drawn inside the scope and
 * outside it alike, such as an error banner (#2033). */
export function useScopeIfAny(): ScopeCtx | undefined {
  return useContext(Ctx);
}

export function useScope(): ScopeCtx {
  const s = useContext(Ctx);
  if (!s) throw new Error("useScope outside ScopeProvider");
  return s;
}

/** The query string for a link to `to`: this page's scope, and the facets
 * both pages understand — or exactly `facets` when the link names its own,
 * which is how a link narrows a page (`facets={{ serial }}`) or widens it
 * back (`facets={{}}`). */
function linkSearch(scope: Scope, from: { pathname: string; search: string }, to: string, facets?: Facets): string {
  return facetSearch(facets ?? carryFacets(from, to), scopeSearch(scope));
}

/** A link to another page at the scope this one shows, carrying the facets
 * the destination understands. `hash` names a place on that page — a model's
 * card on `/device-models` (#354) — and goes after the scope, so the address
 * still carries both. */
export function ScopeLink({ to, facets, hash, ...rest }: Omit<LinkProps, "to"> & { to: string; facets?: Facets; hash?: string }) {
  const { scope } = useScope();
  const location = useLocation();
  return <Link to={{ pathname: to, search: linkSearch(scope, location, to, facets), hash }} {...rest} />;
}

/** A link that picks `scope` and stays on this page — the scope switcher's
 * pick, as an anchor (#1276). Its `href` is the address `setScope` would go
 * to, so a plain click narrows this page and a middle-click or ⌘-click opens
 * a tab already narrowed. The pick is a convenience, not an authorization:
 * the narrowed page's reads carry the scope and the server answers them. */
export function ScopePickLink({ scope, ...rest }: Omit<LinkProps, "to"> & { scope: Scope }) {
  const { pickTarget } = useScope();
  return <Link to={pickTarget(scope)} {...rest} />;
}

/** An entry of the primary navigation: the page whole, at the scope this one
 * shows, and never a facet (#702). A nav entry is a place to be and a facet
 * names a thing to look at, so "Devices" from a device's page is the site's
 * device list and "Clients" from it is every station, not the ones on that
 * device. The in-page links — a device's name, `ObjectLink`, the rail's own
 * "All devices" — are `ScopeLink`, and they still carry what #254 and #263
 * gave them. Until #702 this carried the facets like `ScopeLink` does, and
 * people read the entry as "go to the list" and found the page had not moved. */
export function ScopeNavLink({ to, ...rest }: Omit<NavLinkProps, "to"> & { to: string }) {
  const { scope } = useScope();
  return <NavLink to={{ pathname: to, search: scopeSearch(scope) }} {...rest} />;
}

/** Query filters for the tenancy lists, from the scope. `/customers` and
 * `/sites` take the two levels above a row; the lists that can be narrowed
 * to a site as well take `siteScopeQuery`. */
export function scopeQuery(scope: Scope): { org_id?: string; customer_id?: string } {
  return { org_id: scope.orgId, customer_id: scope.customerId };
}

/** Query filters for the lists that filter at all three levels (`/users`,
 * `/audit`), from the scope. */
export function siteScopeQuery(scope: Scope): { org_id?: string; customer_id?: string; site_id?: string } {
  return { org_id: scope.orgId, customer_id: scope.customerId, site_id: scope.siteId };
}
